import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarClock, Play, X, Plus, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import { API, authHeaders } from './utils';

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const RecurringSettingsDialog = ({ open, onOpenChange, settings, reload }) => {
  const [enabled, setEnabled] = useState(false);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEnabled(!!settings?.enabled);
    setDayOfMonth(settings?.day_of_month || 1);
    setRecipients(settings?.recipients || []);
    setEmailInput('');
  }, [open, settings]);

  const addEmail = () => {
    const e = emailInput.trim();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast({ title: 'Invalid email', description: e, variant: 'destructive' });
      return;
    }
    if (recipients.some(r => r.toLowerCase() === e.toLowerCase())) { setEmailInput(''); return; }
    setRecipients(prev => [...prev, e]);
    setEmailInput('');
  };

  const removeEmail = (e) => setRecipients(prev => prev.filter(x => x !== e));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/invoices/recurring/settings`,
        { enabled, day_of_month: Number(dayOfMonth), recipients },
        { headers: authHeaders() });
      toast({ title: 'Saved', description: 'Auto-invoice settings updated' });
      reload?.();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/api/invoices/recurring/run-now`, {}, { headers: authHeaders() });
      const n = r.data?.generated || 0;
      toast({
        title: n ? `Generated ${n} draft invoice(s)` : 'Nothing to generate',
        description: n ? (r.data?.email === 'sent' ? 'Drafts created & HR emailed.' : 'Drafts created.') : 'No invoices are marked to repeat.',
      });
      reload?.();
    } catch (e) {
      toast({ title: 'Run failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="recurring-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-rose-600" /> Recurring Auto-Invoices
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50/60 p-3">
            <div>
              <p className="font-semibold text-sm text-gray-900">Auto-invoicing</p>
              <p className="text-xs text-gray-500">Master switch for all invoices marked “Repeat monthly”.</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="recurring-master-switch" />
          </div>

          <div>
            <Label className="text-xs">Generate on day of month</Label>
            <Select value={String(dayOfMonth)} onValueChange={(v) => setDayOfMonth(Number(v))}>
              <SelectTrigger data-testid="recurring-day-select" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <SelectItem key={d} value={String(d)}>{ordinal(d)} of each month</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-400 mt-1">
              If a month is shorter, it runs on that month’s last day. New invoices are created as <b>drafts</b> for you to review.
            </p>
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Notify these emails (HR / Finance)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                placeholder="name@company.com"
                data-testid="recurring-email-input"
              />
              <Button type="button" variant="outline" onClick={addEmail} data-testid="recurring-email-add">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {recipients.length === 0 ? (
                <span className="text-[11px] text-gray-400">No recipients yet — drafts will be created but no email sent.</span>
              ) : recipients.map(e => (
                <Badge key={e} variant="outline" className="bg-white gap-1 pr-1" data-testid={`recurring-email-${e}`}>
                  {e}
                  <button onClick={() => removeEmail(e)} className="ml-0.5 text-gray-400 hover:text-rose-600">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {settings?.last_run_at && (
            <p className="text-[11px] text-gray-400" data-testid="recurring-last-run">
              Last run: {new Date(settings.last_run_at).toLocaleString()}
            </p>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={runNow}
            disabled={running}
            className="sm:mr-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="recurring-run-now"
          >
            <Play className="h-4 w-4 mr-1" /> {running ? 'Running…' : 'Generate now'}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="recurring-save">
            {busy ? 'Saving…' : 'Save settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringSettingsDialog;
